import type { WebSocketAdapter } from './WebSocketAdapter';

export interface SavedRoomSessionData {
  roomName: string | null;
  playerName: string | null;
  avatar: string | null;
  hostToken: string | null;
}

const RESTORE_EMIT_DEDUPE_MS = 1500;

let lastRestoreEmitKey = '';
let lastRestoreEmitAt = 0;

/**
 * Emit the canonical restore action for the current saved session.
 * Hosts restore via get-room-state; players restore via join-room.
 * Host-play-too emits both, but duplicate callers are throttled so
 * connect/reconnect/loading fallbacks do not spam out-of-order restores.
 */
export function emitRoomRestore(
  socket: WebSocketAdapter | null,
  savedData: SavedRoomSessionData,
  options?: {
    roomName?: string | null;
    force?: boolean;
  }
): boolean {
  if (!socket?.connected) return false;

  const roomName = options?.roomName ?? savedData.roomName;
  if (!roomName) return false;

  const restoreKey = JSON.stringify({
    roomName,
    hostToken: savedData.hostToken ?? null,
    playerName: savedData.playerName ?? null,
  });
  const now = Date.now();
  if (!options?.force && restoreKey === lastRestoreEmitKey && now - lastRestoreEmitAt < RESTORE_EMIT_DEDUPE_MS) {
    return false;
  }
  lastRestoreEmitKey = restoreKey;
  lastRestoreEmitAt = now;

  if (savedData.hostToken) {
    socket.emit('get-room-state', {
      roomName,
      hostToken: savedData.hostToken || undefined,
    });
    if (savedData.playerName) {
      socket.emit('join-room', {
        roomName,
        playerName: savedData.playerName,
        avatar: savedData.avatar || undefined,
      });
    }
    return true;
  }

  if (savedData.playerName) {
    socket.emit('join-room', {
      roomName,
      playerName: savedData.playerName,
      avatar: savedData.avatar || undefined,
    });
    return true;
  }

  return false;
}
